import { Button } from '@fountain/fountain-ui-components';
import {
  Button as MuiButton,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { StyledPagination } from 'components/StyledPagination';
import globalMessages from 'shared/global/messages';

import { focusAfterRemoval } from '../focusAfterRemoval';
import ruleMessages from '../messages';
import { SubjectTypeChip } from '../SubjectTypeChip';
import { OFFER, OPENING } from '../types';
import { ApprovalStatusCell } from './ApprovalStatusCell';
import { ApproveOpeningsDialog } from './ApproveOpeningsDialog';
import { BulkDecisionBar } from './BulkDecisionBar';
import { Decision } from './DecisionFooter';
import messages from './messages';
import { OfferReviewPanel } from './OfferReviewPanel';
import { OpeningActionsCell } from './OpeningActionsCell';
import { QueueFilterMenu, QueueFilterOption } from './QueueFilterMenu';
import { QueueSearchField } from './QueueSearchField';
import { RejectOpeningDialog } from './RejectOpeningDialog';
import { SelectAllCheckbox } from './SelectAllCheckbox';
import { useStyles } from './styles';
import { ApprovalRequest, ReviewEntry } from './types';
import {
  ALL,
  StatusFilter,
  TypeFilter,
  useApprovalRequests,
} from './useApprovalRequests';
import { useBulkDecision } from './useBulkDecision';
import { isSelectable, useQueueSelection } from './useQueueSelection';

const TYPE_OPTIONS: Array<QueueFilterOption<TypeFilter>> = [
  { value: ALL, label: messages.filterAllTypes },
  { value: OFFER, label: ruleMessages.typeOffer },
  { value: OPENING, label: ruleMessages.typeOpening },
];

const STATUS_OPTIONS: Array<QueueFilterOption<StatusFilter>> = [
  { value: ALL, label: messages.filterAllStatuses },
  { value: 'pending', label: messages.statusPending },
  { value: 'approved', label: globalMessages.approved },
  { value: 'rejected', label: messages.statusRejected },
  { value: 'cancelled', label: messages.statusCancelled },
];

/**
 * The checkbox, Opening, Type, Approver rule, Approval status, and the Actions
 * cell — so a state rendered in the table body spans the full width rather than
 * sitting under the first column.
 */
const COLUMN_COUNT = 6;

/**
 * One waiting approval (Design Requirements §5).
 *
 * ## The first column names the opening, whatever the row is about
 *
 * Not the row's subject. An offer prints the opening it is on with the applicant
 * beneath it; an opening prints the same opening with nothing beneath it — which
 * is why the header reads "Opening" rather than something that would have to
 * change per row. The server answers both from one field for the same reason.
 *
 * ## An offer opens a panel; an opening decides in place
 *
 * An offer is a document somebody has to read before deciding, so its row opens
 * the review panel. An opening has none — the rule and its levels are already in
 * the two columns to the left — so it carries Approve and Reject directly. See
 * `OpeningActionsCell`.
 *
 * The Review control is not gated on whether this viewer can decide: the panel is
 * readable either way, and says which of the three reasons it is showing no
 * decision controls. The opening's controls are gated, because there is nothing
 * else in the cell for somebody with no open assignment to read.
 *
 * ## Four ways in, and the row is the only one that is not a control
 *
 * Design Requirements §5 locks the row, the opening name and the Preview offer
 * letter link; Review is the one that was already there and stays, because it is
 * the affordance a reader finds without hovering. Three of the four are real
 * `<button>`s, so the keyboard reaches the panel three ways.
 *
 * The row is the exception and is a mouse convenience only: the `<tr>` carries
 * no `role` and no `tabIndex`, because making it a widget would nest the button,
 * the link and the checkbox inside one — an axe `nested-interactive` violation,
 * and what made Enter on `TemplateRow`'s delete button both confirm and
 * navigate. Every control inside stops the click, so pressing one never also
 * fires the row.
 *
 * Which one was used rides along to the panel: three of these were added on the
 * strength of the mock, and this is what says whether anybody uses them.
 */
const QueueRow: React.FC<{
  row: ApprovalRequest;
  /** The row's `<tr>`, which the queue moves focus into after a decision. */
  rowRef: React.Ref<HTMLTableRowElement>;
  selected: boolean;
  /** True while a bulk decision is in flight — see `OpeningActionsCell`. */
  busy: boolean;
  onReview: (entry: ReviewEntry) => void;
  /** Re-requests the page. Resolves once the page has been re-read. */
  onDecided: () => Promise<void>;
  onToggle: () => void;
  /** True while this row has a decision of its own in flight. */
  deciding: boolean;
  onDecidingChange: (id: string, deciding: boolean) => void;
}> = ({
  row,
  rowRef,
  selected,
  busy,
  deciding,
  onReview,
  onDecided,
  onToggle,
  onDecidingChange,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  // The row's own identity, for the accessible name of a control that is called
  // "Review" on every row. The applicant first: on an offer row that is the most
  // identifying thing, and every offer has one.
  const rowLabel = row.subject.applicantName ?? row.subject.positionName ?? '';
  // Not `subjectType !== OPENING`: a row of a type this build does not know has
  // no document behind it either, and guessing "offer" would make it clickable
  // to a panel with nothing to show.
  const isOffer = row.subjectType === OFFER;
  // `positionName` is nullable and `normalize.ts` passes an empty string through
  // untouched, so a button built from it alone can come out with no content at
  // all — a zero-width click target with no accessible name (axe `button-name`).
  // The row, the preview link and Review still reach the panel, so the name
  // degrades to the plain line an opening row shows.
  const nameOpensPanel = isOffer && Boolean(row.subject.positionName);

  return (
    <TableRow
      className={classNames({ [styles.rowClickable]: isOffer })}
      data-testid="approval-request-row"
      ref={rowRef}
      onClick={isOffer ? () => onReview('row') : undefined}
    >
      {/* Empty on a row a bulk decision cannot act on, rather than disabled: a
          disabled checkbox on every offer row reads as a permission the reader
          might gain, and there is nothing they could do to gain it. */}
      <TableCell className={classNames(styles.cell, styles.selectCell)}>
        {isSelectable(row) && (
          <Checkbox
            checked={selected}
            color="primary"
            disabled={busy || deciding}
            inputProps={{
              'aria-label': intl.formatMessage(messages.selectRow, {
                opening: row.subject.positionName ?? '',
              }),
            }}
            size="small"
            // §5: selecting a row must not open it. On the control rather than
            // on the cell, which is `RuleRow`'s pattern and matters here — an
            // offer row's cell is empty (only openings are selectable), and a
            // cell that swallowed the click would leave a dead 48px gutter down
            // the left edge of every row the design says is clickable.
            onClick={event => event.stopPropagation()}
            onChange={onToggle}
          />
        )}
      </TableCell>

      <TableCell className={classNames(styles.cell, styles.subjectCell)}>
        {/* The keyboard path into the panel, and the one the design leads with.
            Its accessible name is the visible text and nothing more (WCAG 2.5.3)
            — a speech-input user says what they can read. Two offers on one
            opening therefore give two buttons with the same name; the applicant
            sits in this cell to tell them apart on screen, and the Review
            control on the same row carries the name that names the applicant. */}
        {nameOpensPanel ? (
          <button
            className={styles.nameButton}
            type="button"
            // Stops the row's own handler from opening the panel a second time.
            // Harmless while both do the same thing, except that they no longer
            // quite do: each reports a different entry point.
            onClick={event => {
              event.stopPropagation();
              onReview('name');
            }}
          >
            {row.subject.positionName}
          </button>
        ) : (
          <Typography className={styles.nameText}>
            {row.subject.positionName}
          </Typography>
        )}

        {/* Absent on an opening row rather than blank: an opening is not about a
            person, so there is nothing for the line to hold. */}
        {row.subject.applicantName && (
          <Typography color="textSecondary" variant="body2">
            {row.subject.applicantName}
          </Typography>
        )}

        {/* On every offer row, whether or not the letter has rendered yet
            (Design Requirements §5). It opens the same panel as everything else
            on the row — the panel is where the letter is shown, and it is also
            what says the letter is still being generated. A link that appeared
            only once a document existed would be missing exactly when a reader
            wants to know why. */}
        {isOffer && (
          <button
            aria-label={intl.formatMessage(messages.previewOfferLetterFor, {
              applicant: rowLabel,
            })}
            className={styles.previewLink}
            data-testid="preview-offer-letter"
            type="button"
            onClick={event => {
              event.stopPropagation();
              onReview('preview_link');
            }}
          >
            <VisibilityOutlined aria-hidden fontSize="inherit" />
            <FormattedMessage {...messages.previewOfferLetter} />
          </button>
        )}
      </TableCell>

      <TableCell className={styles.cell}>
        <SubjectTypeChip subjectType={row.subjectType} testId="request-type" />
      </TableCell>

      <TableCell className={styles.cell}>
        {/* One line per rule. Plural on purpose: several rules can gate one
            subject, and each contributes its own levels to the cell beside this
            one — in the same order, so the two read across. A rule the pinned
            snapshot could not name renders nothing rather than a placeholder;
            its levels are still listed. */}
        <div className={styles.ruleNames} data-testid="approver-rules">
          {row.rules.map(
            rule =>
              rule.name && (
                <Typography key={rule.id ?? rule.name} variant="body2">
                  {rule.name}
                </Typography>
              ),
          )}
        </div>
      </TableCell>

      <TableCell className={styles.cell}>
        {/* `status !== 'pending'` is the whole of "settled" — approved, rejected
            and cancelled all end the request, and a cancelled one leaves its
            open level reading `active`. */}
        <ApprovalStatusCell
          requestSettled={row.status !== 'pending'}
          rules={row.rules}
        />
      </TableCell>

      <TableCell className={classNames(styles.cell, styles.actionsCell)}>
        {isOffer && (
          <Button
            autoWidth
            size="small"
            type="secondary"
            // Every row's control reads "Review", so the accessible name carries
            // the row it opens — otherwise a screen reader user hears the same
            // name N times with nothing telling them apart.
            aria-label={intl.formatMessage(messages.reviewOfferFor, {
              applicant: rowLabel,
            })}
            // Same reason as the name button: the row's handler would otherwise
            // open the panel a second time and report the wrong entry point.
            // Annotated because the FDS `Button` is generic over its element, so
            // the handler's parameter is not inferred here.
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onReview('review_button');
            }}
          >
            <FormattedMessage {...messages.reviewOffer} />
          </Button>
        )}
        {row.subjectType === OPENING && (
          <OpeningActionsCell
            disabled={busy}
            row={row}
            onDecided={onDecided}
            onDecidingChange={onDecidingChange}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

/**
 * The approvals a recruiter or approver has a stake in, filtered and paged.
 *
 * Offers and openings together, filtered and searched server-side and paged.
 * An offer row opens the review panel, because an offer is a document somebody
 * has to read; an opening is decided from the row itself, alone or in bulk.
 */
export const ApprovalsQueue: React.FC = () => {
  const styles = useStyles();
  const intl = useIntl();
  // Which request the panel is showing, and which control opened it. One piece
  // of state, so "two panels open" is unrepresentable — and so the entry point
  // cannot drift from the request it describes.
  const [reviewing, setReviewing] = useState<{
    id: string;
    entry: ReviewEntry;
  } | null>(null);

  // On by default: the queue's job is "what needs me" (Design Requirements §5).
  const [mine, setMine] = useState(true);
  const [type, setType] = useState<TypeFilter>(ALL);
  const [status, setStatus] = useState<StatusFilter>(ALL);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, answersFilters, isError, refetch } = useApprovalRequests({
    mine,
    type,
    status,
    search,
    page,
  });

  const rows = data?.rows ?? [];
  // The spinner stands in until the payload in hand answers the filters and
  // page on screen: the first load, a page turn, a filter change. A decision
  // re-reads the same question, so its rows stay up through the request.
  // Blanking the table for that drops every row the reader is working through,
  // and keyboard focus with them.
  const showSpinner = !answersFilters;
  // The pager reads the payload whichever question it answers, so it stays
  // mounted through a page turn — see `useApprovalRequests`, which holds the
  // payload across one.
  const currentPage = data?.currentPage ?? page;
  const totalPages = data?.totalPages ?? 1;

  // Which rows are deciding on their own, owned here rather than per row: the
  // selection has to exclude them, and Select All builds its set from what the
  // selection calls selectable.
  const [decidingIds, setDecidingIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const setDeciding = useCallback((id: string, deciding: boolean) => {
    setDecidingIds(current => {
      if (current.has(id) === deciding) return current;

      const next = new Set(current);
      if (deciding) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const selection = useQueueSelection(rows, decidingIds);
  // Whether either confirmation is open for the selection — the count dialog in
  // front of a bulk approval (Design Requirements §5), and the reason dialog in
  // front of a bulk rejection. One boolean each, and the single-row reject is
  // the row's own state, so "both open" is unrepresentable per surface.
  //
  // Only the rejection outlives its confirm — it holds the reason field and
  // reports a refusal against it, so `onSettled` is what closes it. The approve
  // dialog closes itself on confirm and needs nothing here.
  const [approvingSelection, setApprovingSelection] = useState(false);
  const [rejectingSelection, setRejectingSelection] = useState(false);
  const bulk = useBulkDecision({
    onSettled: () => {
      setRejectingSelection(false);
      selection.clear();
      return refetch();
    },
  });

  // Each row's `<tr>` by request id, and the list around them: where focus goes
  // when a decision's re-read removes the control that held it.
  const rowElements = useRef(new Map<string, HTMLTableRowElement>());
  const listRef = useRef<HTMLDivElement>(null);
  const trackRow = (id: string) => (element: HTMLTableRowElement | null) => {
    if (element) rowElements.current.set(id, element);
    else rowElements.current.delete(id);
  };

  // The row a decision was recorded against, and the order of the rows on
  // screen when it was, set once the page has re-read it.
  const [decided, setDecided] = useState<{
    id: string;
    order: string[];
  } | null>(null);

  // The page is re-requested rather than patched: a decided row leaves a My
  // approvals list, and settles in place in a wider one. The panel asks for the
  // same re-read when an edit closes the request it was showing.
  const rereadAfterDecision = (id: string) => {
    const order = rows.map(row => row.id);
    return refetch().then(() => setDecided({ id, order }));
  };

  // Puts focus back into the list once the re-read lands. By then it is usually
  // on `<body>`: the re-read removed the Review button a closing panel handed it
  // back to, or the browser dropped it from an Approve disabled for the write.
  // Left there, a keyboard user starts again from the top of the page. It goes
  // to the decided row if that is still listed, then to the rows that followed
  // it, then to the ones above it, nearest first, then to the list itself.
  //
  // Not while the panel is open. An edit's re-read lands with the panel still
  // up, and a control that unmounts inside it leaves focus on `<body>` as well:
  // moving it to a row behind the drawer takes the keyboard out of the panel.
  // So this waits for the panel to close, which hands focus back to its opener
  // when that row is still listed.
  //
  // `focusAfterRemoval` acts only while focus is on `<body>`, so it never takes
  // focus from a control the reader has moved to.
  useEffect(() => {
    if (!decided || reviewing) return;
    setDecided(null);

    // An id the list did not hold anchors at the top.
    const at = Math.max(decided.order.indexOf(decided.id), 0);
    focusAfterRemoval(
      [...decided.order.slice(at), ...decided.order.slice(0, at).reverse()].map(
        id => rowElements.current.get(id),
      ),
      () => listRef.current,
    );
  }, [decided, reviewing]);

  const decideSelection = (decision: Decision, comment?: string) =>
    bulk.run(selection.selected, decision, comment);

  // Everything that could change what a run is deciding, or land its completion
  // somewhere else, is held while it is in flight: the rows' own controls, the
  // checkboxes, the pager and the filters. The alternative was cancelling the run
  // on a page turn, which cannot un-commit the decisions that already landed.
  const busy = bulk.running !== null;

  // Any filter change invalidates the page: page 3 of the unfiltered queue is
  // very often past the end of the filtered one, and a request that kept the
  // page would answer with nothing while the toolbar claimed a filter had
  // matched nothing.
  //
  // It also invalidates the selection, and so does a page turn: `useQueueSelection`
  // will not act on a row that is off screen, but a count that survives the rows
  // it counted is a count the reader has to reconcile against a table that no
  // longer shows them.
  const applyFilter = (change: () => void) => {
    change();
    setPage(1);
    selection.clear();
    bulk.clearOutcome();
  };

  const goToPage = (next: number) => {
    setPage(next);
    selection.clear();
    bulk.clearOutcome();
  };

  const isFiltered = type !== ALL || status !== ALL || search !== '';

  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.toolbarFilters}>
          {/* A toggle, not one of a set: My approvals is on or off, which is
              exactly what `aria-pressed` expresses. The exclusive Offers |
              Openings control on the sibling rules tab is a radio group for the
              opposite reason — it can never be neither.

              MUI's `Button`, not the FDS one this file imports for the row
              controls: the two pills beside it are `QueueFilterMenu`'s, which
              are MUI's, and `filterPill` is one class meant to style all three
              alike. The FDS button holds a fixed 148px width at `medium` and a
              shadow on every size, either of which sets this pill apart from
              the two next to it. */}
          <MuiButton
            aria-pressed={mine}
            className={classNames(styles.filterPill, {
              [styles.filterPillActive]: mine,
            })}
            data-testid="my-approvals-toggle"
            disabled={busy}
            variant="outlined"
            onClick={() => applyFilter(() => setMine(current => !current))}
          >
            <FormattedMessage {...messages.myApprovals} />
          </MuiButton>

          <QueueFilterMenu
            allValue={ALL}
            disabled={busy}
            label={messages.filterType}
            options={TYPE_OPTIONS}
            testId="type-filter"
            value={type}
            onChange={next => applyFilter(() => setType(next))}
          />

          <QueueFilterMenu
            allValue={ALL}
            disabled={busy}
            label={globalMessages.status}
            options={STATUS_OPTIONS}
            testId="status-filter"
            value={status}
            onChange={next => applyFilter(() => setStatus(next))}
          />
        </div>

        {/* The page reset lands here, synchronously with the new term, rather
            than inside the field's own debounce. */}
        <QueueSearchField
          value={search}
          onChange={next => applyFilter(() => setSearch(next))}
        />
      </div>

      {/* Only while something is selected, so it costs nothing when it is not in
          use — and it replaces the summary, because a bar offering a decision
          above a line reporting the last one reads as one statement. */}
      {bulk.running !== null || selection.selected.length > 0 ? (
        <BulkDecisionBar
          count={bulk.running?.count ?? selection.selected.length}
          running={bulk.running?.decision ?? null}
          // Neither button decides anything by itself: each opens the
          // confirmation §5 puts in front of a bulk decision — the count for an
          // approval, the count and the shared reason for a rejection.
          onApprove={() => setApprovingSelection(true)}
          onClear={selection.clear}
          onReject={() => setRejectingSelection(true)}
        />
      ) : (
        bulk.outcome && (
          <div className={styles.bulkSummary}>
            {/* `status` when everything landed and `alert` when it did not: a
                partial run has something the reader must go and look at, and the
                two roles are how that difference reaches a screen reader. */}
            <Typography
              role={
                bulk.outcome.succeeded === bulk.outcome.requested
                  ? 'status'
                  : 'alert'
              }
              variant="body2"
            >
              {bulk.outcome.succeeded === bulk.outcome.requested ? (
                <FormattedMessage
                  {...(bulk.outcome.decision === 'approved'
                    ? messages.bulkAllApproved
                    : messages.bulkAllRejected)}
                  values={{ count: bulk.outcome.succeeded }}
                />
              ) : (
                <FormattedMessage
                  {...messages.bulkPartial}
                  values={{
                    succeeded: bulk.outcome.succeeded,
                    requested: bulk.outcome.requested,
                  }}
                />
              )}
            </Typography>
            <Button
              autoWidth
              size="small"
              type="secondary"
              onClick={bulk.clearOutcome}
            >
              <FormattedMessage {...globalMessages.close} />
            </Button>
          </div>
        )
      )}

      {/* Above the list, not inside it: the failure is about the request, not
          about a row, and burying it in the table body reads as "no approvals" —
          which is the one thing it does not mean. */}
      {isError && (
        <div className={styles.error} role="alert">
          <Typography variant="body1">
            <FormattedMessage {...messages.loadError} />
          </Typography>
          <Button autoWidth size="small" type="secondary" onClick={refetch}>
            <FormattedMessage {...messages.retry} />
          </Button>
        </div>
      )}

      {/* One table for the loading, empty and populated states rather than a
          table for rows and a bare div for the rest: the column headers are what
          say what the rows will hold, so dropping them mid-request makes the
          region change shape twice per load.

          A failed request is the exception — the banner above has already
          explained itself, and headers over a zero-row body would read as a
          second fault.

          `role="region"` with a `tabIndex` because this container scrolls: the
          Position column holds account-authored text, so `overflowX: auto`
          genuinely engages on a narrow window, and in the loading and empty
          states there is nothing focusable inside at all (WCAG 2.1.1). */}
      {!isError && (
        <div
          aria-label={intl.formatMessage(messages.approvalsTab)}
          className={styles.list}
          ref={listRef}
          role="region"
          tabIndex={0}
        >
          <Table
            aria-label={intl.formatMessage(messages.approvalsTab)}
            className={styles.table}
          >
            <TableHead>
              <TableRow>
                {/* Covers only the rows a bulk decision can act on. Absent when
                    there are none — a checkbox that selects nothing is a control
                    with no state to be in. */}
                <TableCell
                  className={classNames(styles.headerCell, styles.selectCell)}
                >
                  <span className={styles.visuallyHidden}>
                    <FormattedMessage {...messages.columnSelect} />
                  </span>
                  {selection.selectable.length > 0 && (
                    <SelectAllCheckbox
                      checked={
                        selection.selected.length ===
                        selection.selectable.length
                      }
                      // False when nothing is ticked AND when everything is: it
                      // is the "some" state, and a checkbox stuck in it says the
                      // page holds a selection nobody made.
                      indeterminate={
                        selection.selected.length > 0 &&
                        selection.selected.length < selection.selectable.length
                      }
                      disabled={busy}
                      label={intl.formatMessage(messages.selectAll)}
                      onChange={selection.toggleAll}
                    />
                  )}
                </TableCell>
                <TableCell className={styles.headerCell}>
                  <FormattedMessage {...messages.columnOpening} />
                </TableCell>
                <TableCell className={styles.headerCell}>
                  <FormattedMessage {...ruleMessages.columnType} />
                </TableCell>
                <TableCell className={styles.headerCell}>
                  <FormattedMessage {...messages.columnApproverRule} />
                </TableCell>
                <TableCell className={styles.headerCell}>
                  <FormattedMessage {...messages.columnApprovalStatus} />
                </TableCell>
                {/* Named for a screen reader reading the table column by column,
                    and unnamed on screen: the control under it is
                    self-describing, and an empty `<th>` reads as a gap. */}
                <TableCell
                  className={classNames(styles.headerCell, styles.actionsCell)}
                >
                  <span className={styles.visuallyHidden}>
                    <FormattedMessage {...messages.columnActions} />
                  </span>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {showSpinner && (
                <TableRow>
                  <TableCell
                    className={styles.stateCell}
                    colSpan={COLUMN_COUNT}
                  >
                    <CircularProgress
                      size={20}
                      aria-label={intl.formatMessage(messages.loadingApprovals)}
                    />
                  </TableCell>
                </TableRow>
              )}

              {!showSpinner && rows.length === 0 && (
                <TableRow>
                  <TableCell className={styles.empty} colSpan={COLUMN_COUNT}>
                    {/* A filter that matched nothing is a different state from a
                        queue with nothing in it: one is recoverable by widening
                        the filter, the other by nobody. `layout-composition`
                        treats the no-data and zero-result cases as separate for
                        exactly that reason. */}
                    {/* `h3` for size — this theme's `h6` is 12px, which would
                        leave the title smaller than the sentence under it. */}
                    <Typography variant="h3">
                      {/* Quoting the term when there is one: "no approvals
                          match these filters" over a searched list leaves the
                          reader checking the menus for a narrowing that is
                          actually in the text field. */}
                      {search !== '' ? (
                        <FormattedMessage
                          {...messages.noSearchMatchesTitle}
                          values={{ term: search }}
                        />
                      ) : (
                        <FormattedMessage
                          {...(isFiltered
                            ? messages.noMatchesTitle
                            : messages.emptyTitle)}
                        />
                      )}
                    </Typography>
                    <Typography
                      className={styles.emptyBody}
                      color="textSecondary"
                      variant="body2"
                    >
                      <FormattedMessage
                        {...(isFiltered
                          ? messages.noMatchesBody
                          : messages.emptyBody)}
                      />
                    </Typography>
                    {/* The sentence above names the way out; this is that
                        sentence made clickable, which is what
                        `layout-composition` asks a filtered-empty state for.
                        Nothing on the genuinely-empty state: no action would make
                        rows appear. `mine` is deliberately not cleared — it is
                        the tab's own default, not a filter the reader set. */}
                    {isFiltered && (
                      <Button
                        autoWidth
                        className={styles.emptyAction}
                        size="small"
                        type="secondary"
                        onClick={() =>
                          applyFilter(() => {
                            setType(ALL);
                            setStatus(ALL);
                            setSearch('');
                          })
                        }
                      >
                        <FormattedMessage {...messages.clearFilters} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}

              {!showSpinner &&
                rows.map(row => (
                  <QueueRow
                    busy={busy}
                    key={row.id}
                    row={row}
                    rowRef={trackRow(row.id)}
                    selected={selection.isSelected(row.id)}
                    deciding={decidingIds.has(row.id)}
                    onDecided={() => rereadAfterDecision(row.id)}
                    onDecidingChange={setDeciding}
                    onReview={entry => setReviewing({ id: row.id, entry })}
                    onToggle={() => selection.toggle(row.id)}
                  />
                ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Only past one page, per `layout-composition`: a pager over a single page
          of results is chrome with nothing behind it. No rows-per-page control —
          the endpoint fixes its own page size, so offering one would be a control
          the server ignores.

          This replaces the "Showing N of M" line the capped list carried. That
          line existed because the list was truncated and had to say so; a paged
          list is not partial, it is one page of a whole the pager can reach. */}
      {!isError && totalPages > 1 && (
        <StyledPagination
          containerClass={styles.pagination}
          currentPage={currentPage}
          disabled={busy}
          lastPage={totalPages}
          setCurrentPage={goToPage}
        />
      )}

      {/* The count, agreed to before anything is recorded (§5). Only the bulk
          path confirms — a row's own Approve stays immediate, per the same
          decision. Confirm closes it and starts the run in one press: the bar's
          running state is the in-flight UI, and a refusal lands in the outcome
          summary, so the dialog has no job left once it is agreed to. */}
      {approvingSelection && (
        <ApproveOpeningsDialog
          count={selection.selected.length}
          openingName={selection.selected[0]?.subject.positionName ?? null}
          onCancel={() => setApprovingSelection(false)}
          onConfirm={() => {
            setApprovingSelection(false);
            decideSelection('approved');
          }}
        />
      )}

      {/* One reason, recorded against every selected opening. The same dialog the
          single-row Reject opens — a second one is a second chance for the two to
          disagree about whether the reason is required. */}
      {rejectingSelection && (
        <RejectOpeningDialog
          count={selection.selected.length}
          openingName={selection.selected[0]?.subject.positionName ?? null}
          submitting={busy}
          onCancel={() => setRejectingSelection(false)}
          onConfirm={reason => decideSelection('rejected', reason)}
        />
      )}

      {/* Keyed so opening a different row remounts the panel: it reads the
          request id into its own state once, on mount, and a reused instance
          would keep showing the previous offer. */}
      {reviewing && (
        <OfferReviewPanel
          key={reviewing.id}
          entry={reviewing.entry}
          requestId={reviewing.id}
          onClose={() => setReviewing(null)}
          // The row is gone from this list once the decision lands — the queue is
          // "waiting on you" — so the list is re-requested rather than patched.
          onDecided={() => rereadAfterDecision(reviewing.id)}
        />
      )}
    </>
  );
};

export default ApprovalsQueue;
